import { useState } from 'react';
import { getAddress, isAddress, type Address } from 'viem';
import { useConnection, useReadContract, useReadContracts } from 'wagmi';
import { inviteCode, normalizeInviteCode, referralManagerAbi } from '@syntra/abi';
import { useLocation } from '../app/router.tsx';
import { ActionButton } from '../components/tx/ActionButton.tsx';
import { CompareBars } from '../components/ui/charts.tsx';
import { CountUp } from '../components/ui/motion.tsx';
import { Icon } from '../components/ui/Icon.tsx';
import { Tree3D, type TreeMember, type TreeSlot } from '../components/referral/Tree3D.tsx';
import { Button, Card, Notice, Pill, Progress, Rows, SectionHead, Segmented, StatTile } from '../components/ui/primitives.tsx';
import { RingGauge } from '../components/ui/viz.tsx';
import { DEPLOYMENT as D } from '../config/env.ts';
import { storedInvite, useInviteSponsor } from '../hooks/useInvite.ts';
import { usePosition } from '../hooks/usePosition.ts';
import { useTeam, type TeamMember } from '../hooks/useTeam.ts';
import { useRank } from '../hooks/useRank.ts';
import { useProtocol } from '../hooks/useProtocol.ts';
import { useI18n, type MessageKey } from '../i18n/index.tsx';
import { E18, fmtBps, fmtPct, fmtToken, fmtUsd, shortAddr, toNum } from '../lib/format.ts';
import { RankEmblem } from '../components/ui/RankEmblem.tsx';
import { PriceMoving } from '../components/ui/PriceMoving.tsx';

const ZERO = '0x0000000000000000000000000000000000000000';
export const RANK_KEYS: MessageKey[] = ['ref.member', 'ref.bronze', 'ref.silver', 'ref.gold', 'ref.diamond', 'ref.crown'];

export interface NodeData {
  sponsor: Address;
  parent: Address;
  side: number;
  rank: number;
  bound: boolean;
  leftChild: Address;
  rightChild: Address;
  leftActiveUsd: bigint;
  rightActiveUsd: bigint;
  leftCumUsd: bigint;
  rightCumUsd: bigint;
  directActiveUsd: bigint;
  ownActiveUsd: bigint;
  highWaterUsd: bigint;
  capUsedUsd: bigint;
  accruedUsd: bigint;
}

/** How far a node is towards a rank: the total of both legs and the weaker side each against their bar. */
export function rankProgress(left: bigint, right: bigint, threshold: bigint, minSideBps: bigint) {
  const weaker = left < right ? left : right;
  const sideBar = (threshold * minSideBps) / 10_000n;
  const total = left + right;
  return {
    total: threshold > 0n ? Math.min(1, Number((total * 10_000n) / threshold) / 10_000) : 0,
    weaker: sideBar > 0n ? Math.min(1, Number((weaker * 10_000n) / sideBar) / 10_000) : 0,
    reached: total >= threshold && weaker * 10_000n >= threshold * minSideBps,
  };
}

/** The invite link the sponsor shares: their short invite code (never the address) and the side new members join. */
export function inviteLink(origin: string, sponsor: Address, side: 'L' | 'R'): string {
  return `${origin}/r/${inviteCode(sponsor)}/${side === 'L' ? 'left' : 'right'}`;
}

/** `/r/<code>[/left|/right]` → the code and side, or null for any other path. */
export function parseInvitePath(path: string): { code: string; side: 'L' | 'R' } | null {
  const m = /^\/r\/([^/]+)(?:\/(left|right))?$/i.exec(path);
  const code = m ? normalizeInviteCode(m[1]) : null;
  return code ? { code, side: m![2]?.toLowerCase() === 'right' ? 'R' : 'L' } : null;
}

function useNode(address: Address | undefined) {
  return useReadContract({
    address: D.referral,
    abi: referralManagerAbi,
    functionName: 'node',
    args: [address ?? ZERO],
    query: { enabled: Boolean(address), refetchInterval: 15_000 },
  });
}

export function Referral() {
  const { t } = useI18n();
  const { address } = useConnection();
  const { data: node } = useNode(address);
  const n = node as NodeData | undefined;
  const { data: me } = usePosition(address);
  const cfg = useReadContracts({
    allowFailure: false,
    contracts: [
      { address: D.referral, abi: referralManagerAbi, functionName: 'directBps' },
      { address: D.referral, abi: referralManagerAbi, functionName: 'minSideBps' },
      { address: D.referral, abi: referralManagerAbi, functionName: 'capMultiplierBps' },
      { address: D.referral, abi: referralManagerAbi, functionName: 'directCount', args: [address ?? ZERO] },
    ],
  });
  const rankQ = useReadContracts({
    allowFailure: false,
    contracts: [1, 2, 3, 4, 5].map((r) => ({ address: D.referral, abi: referralManagerAbi, functionName: 'rankConfig', args: [r] }) as const),
  });
  const [directBps, minSideBps, capBps, directCount] = cfg.data ?? [];
  const ranks = (rankQ.data ?? []) as readonly { thresholdUsd: bigint; rateBps: number }[];

  const cap = n ? (n.highWaterUsd * BigInt(capBps ?? 30_000)) / 10_000n : 0n;
  const matched = n ? (n.leftActiveUsd < n.rightActiveUsd ? n.leftActiveUsd : n.rightActiveUsd) : undefined;
  return (
    <>
      <SectionHead eyebrow={t('ref.eyebrow')} title={t('ref.title')} />
      <PriceMoving />
      {address && n ? (
        <div className="grid tiles">
          <StatTile label={t('ref.rank')} value={t(RANK_KEYS[n.rank] ?? 'ref.member')} />
          <StatTile label={t('ref.left')} value={fmtUsd(n.leftActiveUsd)} />
          <StatTile label={t('ref.right')} value={fmtUsd(n.rightActiveUsd)} />
          <StatTile label={t('ref.matched')} value={fmtUsd(matched)} />
          <StatTile label={t('ref.claimable')} value={fmtUsd(me.referralPendingUsd)} />
          <StatTile
            label={t('ref.direct')}
            value={directCount !== undefined ? String(directCount) : '—'}
            sub={directBps !== undefined ? t('ref.directSub', { bps: fmtBps(directBps) }) : undefined}
          />
        </div>
      ) : null}

      <div className="grid two">
        <div className="grid">
          {address && n && !n.bound ? <BindCard /> : <InviteCard bound={Boolean(n?.bound)} parent={n?.parent} side={n?.side} sponsor={n?.sponsor} />}
          {address && n?.bound ? <TeamCard me={address} /> : null}
          {address && n ? (
            <Card title={t('ref.cap')}>
              <Progress value={cap > 0n ? Number((n.capUsedUsd * 10_000n) / cap) / 10_000 : 0} />
              <p className="soft" style={{ fontSize: 13 }}>
                {t('ref.capUsed', { used: fmtUsd(n.capUsedUsd), cap: fmtUsd(cap) })}
              </p>
              <p className="muted" style={{ fontSize: 12, marginBottom: 0 }}>
                {t('ref.capNote')}
              </p>
            </Card>
          ) : null}
          {address ? <ClaimCard pendingUsd={me.referralPendingUsd} active={(n?.ownActiveUsd ?? 0n) > 0n} /> : null}
          <EarnCard directBps={directBps} capBps={capBps} ranks={ranks} />
        </div>
        <div className="grid">
          <RankCard node={address ? n : undefined} />
          <Card title={t('ref.ladder')}>
            <div className="field" style={{ gap: 14 }}>
              {ranks.map((r, i) => {
                const pr = n ? rankProgress(n.leftActiveUsd, n.rightActiveUsd, r.thresholdUsd, BigInt(minSideBps ?? 2500)) : null; // ranks use what both legs hold now (R-1)
                return (
                  <div key={i} className="field" style={{ gap: 6 }} data-testid={`rank-${i + 1}`}>
                    <div className="row">
                      <b className="rank-name">
                        <RankEmblem rank={i + 1} size={34} reached={Boolean(pr?.reached || (n && n.rank >= i + 1))} />
                        {t(RANK_KEYS[i + 1])} {pr?.reached || (n && n.rank >= i + 1) ? <Pill tone="green">{t('ref.reached')}</Pill> : null}
                      </b>
                      <span className="soft" style={{ fontSize: 13 }}>
                        {t('ref.rate')} {fmtBps(r.rateBps)} · {t('ref.needs')} {fmtUsd(r.thresholdUsd)}
                      </span>
                    </div>
                    {pr ? (
                      <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                        <div>
                          <span className="muted" style={{ fontSize: 11 }}>
                            {t('ref.total')}
                          </span>
                          <Progress value={pr.total} />
                        </div>
                        <div>
                          <span className="muted" style={{ fontSize: 11 }}>
                            {t('ref.weaker')}
                          </span>
                          <Progress value={pr.weaker} tone="green" />
                        </div>
                      </div>
                    ) : null}
                  </div>
                );
              })}
              <p className="muted" style={{ fontSize: 12, margin: 0 }}>
                {t('ref.ladderNote', { pct: fmtBps(minSideBps ?? 2500) })}
              </p>
            </div>
          </Card>
          {address && n?.bound ? <TreeCard me={address} /> : null}
        </div>
      </div>
    </>
  );
}

/** The wallet's rank as a glowing ring of progress toward the next one, with both legs' active volume as bars. */
function RankCard({ node }: { node: NodeData | undefined }) {
  const { t } = useI18n();
  const { address } = useConnection();
  const rank = useRank(address);
  const name = t(RANK_KEYS[rank?.rank ?? 0] ?? 'ref.member');
  return (
    <Card className="rank-card">
      <div className="card-head">
        <h3>{t('ref.yourRank')}</h3>
        {rank?.next ? (
          <Pill>
            <Icon name="star" width={13} height={13} />
            {t('ref.nextRate', { rank: t(RANK_KEYS[rank.next.rank]), rate: fmtBps(rank.next.rateBps) })}
          </Pill>
        ) : null}
      </div>
      <div className="rank-body">
        <RingGauge fraction={rank?.progress ?? 0} size={168} stroke={14} label={t('ref.yourRank')}>
          <RankEmblem rank={rank?.rank ?? 0} size={38} />
          <span className="v">{name}</span>
          <span className="l num">
            {rank?.next ? (
              <>
                <CountUp value={rank.progress * 100} format={(v) => `${Math.round(v)}%`} /> {t('ref.toNext')}
              </>
            ) : rank ? (
              t('ref.topRankShort')
            ) : (
              '—'
            )}
          </span>
        </RingGauge>
        <div className="rank-legs">
          {node ? (
            <>
              <span className="k">{t('ref.legs')}</span>
              <CompareBars
                rows={[
                  { label: t('ref.placeLeft'), value: toNum(node.leftActiveUsd), display: fmtUsd(node.leftActiveUsd), color: 'var(--chart-1)' },
                  { label: t('ref.placeRight'), value: toNum(node.rightActiveUsd), display: fmtUsd(node.rightActiveUsd), color: 'var(--chart-2)' },
                ]}
              />
              <span className="soft" style={{ fontSize: 12 }}>
                {t('ref.weakerShare', {
                  pct: fmtPct(
                    node.leftActiveUsd + node.rightActiveUsd > 0n
                      ? toNum(node.leftActiveUsd < node.rightActiveUsd ? node.leftActiveUsd : node.rightActiveUsd) / toNum(node.leftActiveUsd + node.rightActiveUsd)
                      : 0,
                    0,
                  ),
                })}
              </span>
            </>
          ) : (
            <p className="soft" style={{ margin: 0 }}>
              {t('ref.rankConnect')}
            </p>
          )}
        </div>
      </div>
    </Card>
  );
}

/** The three ways referral rewards accrue, with every figure read from the contract. */
function EarnCard({ directBps, capBps, ranks }: { directBps?: number | bigint; capBps?: number | bigint; ranks: readonly { rateBps: number }[] }) {
  const { t } = useI18n();
  const show = (v: number | bigint | undefined) => (v !== undefined ? fmtBps(v) : '—');
  const rates = ranks.map((r) => Number(r.rateBps));
  const items: { icon: 'referral' | 'star' | 'stake'; title: string; body: string }[] = [
    { icon: 'referral', title: t('ref.earnDirect'), body: t('ref.earnDirectP', { pct: show(directBps) }) },
    {
      icon: 'star',
      title: t('ref.earnBinary'),
      body: t('ref.earnBinaryP', { min: rates.length ? fmtBps(Math.min(...rates)) : '—', max: rates.length ? fmtBps(Math.max(...rates)) : '—' }),
    },
    { icon: 'stake', title: t('ref.earnCap'), body: t('ref.earnCapP', { pct: show(capBps) }) },
  ];
  return (
    <Card title={t('ref.earnTitle')}>
      <ul className="earn-list">
        {items.map((it) => (
          <li key={it.icon}>
            <span className="icon-badge" aria-hidden="true">
              <Icon name={it.icon} width={18} height={18} />
            </span>
            <span>
              <b>{it.title}</b>
              <span className="soft">{it.body}</span>
            </span>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function InviteCard({ bound, parent, side, sponsor }: { bound: boolean; parent?: Address; side?: number; sponsor?: Address }) {
  const { t } = useI18n();
  const { address } = useConnection();
  const [place, setPlace] = useState<'L' | 'R'>('L');
  const [copied, setCopied] = useState<'link' | 'code' | null>(null);
  if (!address) {
    return (
      <Card title={t('ref.inviteTitle')}>
        <p className="soft">{t('common.connectFirst')}</p>
      </Card>
    );
  }
  if (!bound) {
    return (
      <Card title={t('ref.inviteTitle')}>
        <p className="soft">{t('ref.needStake')}</p>
      </Card>
    );
  }
  const link = inviteLink(window.location.origin, address, place);
  const code = inviteCode(address);
  const copy = async (what: 'link' | 'code') => {
    await navigator.clipboard?.writeText(what === 'link' ? link : code).catch(() => undefined);
    setCopied(what);
    setTimeout(() => setCopied(null), 1500);
  };
  return (
    <Card title={t('ref.inviteTitle')} highlight>
      <p className="soft" style={{ marginTop: 0 }}>
        {t('ref.inviteAbout')}
      </p>
      <div className="field" style={{ gap: 10 }}>
        <div className="invite-code">
          <span className="field-top">{t('ref.yourCode')}</span>
          <b className="num" data-testid="invite-code">
            {code}
          </b>
          <span className="muted">{t('ref.codeNote', { address: shortAddr(address) })}</span>
        </div>
        <Segmented<'L' | 'R'>
          label={t('ref.side')}
          value={place}
          onChange={setPlace}
          options={[
            { value: 'L', label: t('ref.placeLeft') },
            { value: 'R', label: t('ref.placeRight') },
          ]}
        />
        <p className="muted side-hint">{t('ref.sideHint')}</p>
        <input className="text-input num" readOnly value={link} aria-label={t('ref.inviteTitle')} onFocus={(e) => e.target.select()} />
        <div className="invite-actions">
          <Button variant="secondary" onClick={() => copy('link')}>
            {copied === 'link' ? t('ref.copied') : t('ref.copy')}
          </Button>
          <Button variant="ghost" onClick={() => copy('code')}>
            {copied === 'code' ? t('ref.copied') : t('ref.copyCode')}
          </Button>
        </div>
        {sponsor && sponsor !== ZERO ? (
          <span className="muted" style={{ fontSize: 12 }} data-testid="my-place">
            {t('ref.bound')}: <span className="num">{shortAddr(sponsor)}</span> ·{' '}
            {parent && parent !== ZERO
              ? t('ref.placedUnder', { parent: shortAddr(parent), side: side === 1 ? t('ref.placeRight') : t('ref.placeLeft') })
              : t('ref.notPlacedYet')}
          </span>
        ) : null}
      </div>
    </Card>
  );
}

/** Everyone the wallet invited: the members placed in its team, and those who joined but have not staked yet. */
function TeamCard({ me }: { me: Address }) {
  const { t } = useI18n();
  const team = useTeam(me);
  const rows = (members: TeamMember[], pending: boolean) => (
    <table className="table stack" data-testid={pending ? 'team-pending' : 'team-placed'}>
      <thead>
        <tr>
          <th>{t('ref.teamMember')}</th>
          <th>{t('ref.teamSide')}</th>
          {pending ? null : <th>{t('ref.teamStake')}</th>}
        </tr>
      </thead>
      <tbody>
        {members.map((m) => (
          <tr key={m.address}>
            <td data-label={t('ref.teamMember')} className="num" title={m.address}>
              {shortAddr(m.address)}
            </td>
            <td data-label={t('ref.teamSide')}>{m.side === 1 ? t('ref.placeRight') : t('ref.placeLeft')}</td>
            {pending ? null : (
              <td data-label={t('ref.teamStake')} className="num">
                {fmtUsd(m.activeUsd)} · {t(RANK_KEYS[m.rank] ?? 'ref.member')}
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );
  return (
    <Card title={t('ref.teamTitle')}>
      <div className="field" style={{ gap: 12 }} data-testid="team">
        {team.placed.length + team.pending.length === 0 ? <p className="soft" style={{ margin: 0 }}>{team.loading ? t('common.loading') : t('ref.teamEmpty')}</p> : null}
        {team.placed.length ? (
          <div className="field">
            <b>{t('ref.teamPlaced')}</b>
            {rows(team.placed, false)}
          </div>
        ) : null}
        {team.pending.length ? (
          <div className="field">
            <b>{t('ref.teamPending')}</b>
            <span className="muted" style={{ fontSize: 12 }}>
              {t('ref.teamPendingNote')}
            </span>
            {rows(team.pending, true)}
          </div>
        ) : null}
      </div>
    </Card>
  );
}

function BindCard() {
  const { t } = useI18n();
  const { search } = useLocation();
  // the link's code, or the invite remembered from an earlier visit through it
  const remembered = storedInvite();
  const [sponsorInput, setSponsor] = useState(search.get('ref') ?? remembered?.code ?? '');
  const [side, setSide] = useState<'L' | 'R'>((search.get('ref') ? search.get('side') : remembered?.side) === 'R' ? 'R' : 'L');
  // an invite code (what links carry) or, typed by hand, the referrer's address
  const typedAddress = isAddress(sponsorInput) ? getAddress(sponsorInput) : undefined;
  const code = typedAddress ? null : normalizeInviteCode(sponsorInput);
  const lookup = useInviteSponsor(code);
  const sponsor = typedAddress ?? (code ? (lookup.data ?? undefined) : undefined);
  const sponsorBound = useReadContract({
    address: D.referral,
    abi: referralManagerAbi,
    functionName: 'isBound',
    args: [sponsor ?? ZERO],
    query: { enabled: Boolean(sponsor) },
  });
  const blocked =
    !typedAddress && !code
      ? t('ref.badAddress')
      : code && lookup.isPending
        ? t('ref.lookingUp')
        : code && lookup.isError
          ? t('ref.lookupFailed')
          : code && lookup.data === null
            ? t('ref.codeUnknown')
            : sponsorBound.data === false
              ? t('ref.sponsorUnknown')
              : null;
  return (
    <Card title={t('ref.bindTitle')} highlight>
      <p className="soft" style={{ marginTop: 0 }}>
        {t('ref.bindAbout')}
      </p>
      <div className="field" style={{ gap: 10 }}>
        <label className="field">
          <span className="field-top">{t('ref.sponsor')}</span>
          <input className="text-input num" value={sponsorInput} onChange={(e) => setSponsor(e.target.value.trim())} placeholder={t('ref.sponsorHint')} />
        </label>
        {sponsor && sponsorBound.data === true ? <Notice tone="ok">{t('ref.sponsorOk', { address: shortAddr(sponsor) })}</Notice> : null}
        <Segmented<'L' | 'R'>
          label={t('ref.side')}
          value={side}
          onChange={setSide}
          options={[
            { value: 'L', label: t('ref.placeLeft') },
            { value: 'R', label: t('ref.placeRight') },
          ]}
        />
        <p className="muted side-hint">{t('ref.sideHintJoin')}</p>
        <ActionButton
          label={t('ref.bindCta')}
          blocked={blocked}
          request={sponsor ? { address: D.referral, abi: referralManagerAbi, functionName: 'bind', args: [sponsor, side === 'L' ? 0 : 1] } : null}
        />
      </div>
    </Card>
  );
}

function ClaimCard({ pendingUsd, active }: { pendingUsd?: bigint; active: boolean }) {
  const { t } = useI18n();
  const { data: p } = useProtocol();
  // a claim converts at the higher of the average and the spot price (audit M-1)
  const claimPrice = p.price !== undefined && p.spot !== undefined && p.spot > p.price ? p.spot : p.price;
  const tokens = pendingUsd !== undefined && claimPrice ? (pendingUsd * E18) / claimPrice : undefined;
  return (
    <Card title={t('ref.claimTitle')}>
      <Rows
        items={[
          [t('ref.claimable'), <b key="c">{fmtUsd(pendingUsd)}</b>],
          ['', tokens !== undefined ? t('ref.approx', { amount: fmtToken(tokens) }) : '—'],
        ]}
      />
      <p className="muted" style={{ fontSize: 12 }}>
        {t('ref.claimAbout')}
      </p>
      <ActionButton
        label={t('ref.claimCta')}
        blocked={!active ? t('ref.notEligible') : !pendingUsd ? t('stake.nothingPending') : null}
        request={{ address: D.referral, abi: referralManagerAbi, functionName: 'claim' }}
      />
    </Card>
  );
}

/** Two levels of the placement tree below `root`, read straight from the contract, shown as a 3D scene. */
function TreeCard({ me }: { me: Address }) {
  const { t } = useI18n();
  const [root, setRoot] = useState<Address>(me);
  const { data: r } = useNode(root);
  const rn = r as NodeData | undefined;
  const kids = rn ? [rn.leftChild, rn.rightChild] : [];
  const nodeReads = (addrs: Address[]) =>
    addrs.map((k) => ({ address: D.referral, abi: referralManagerAbi, functionName: 'node', args: [k] }) as const);
  const q = useReadContracts({
    allowFailure: false,
    query: { enabled: kids.some((k) => k !== ZERO), refetchInterval: 15_000 },
    contracts: nodeReads(kids),
  });
  const kidNodes = (q.data ?? []) as unknown as NodeData[];
  const grand = kids.flatMap((k, i) => (k === ZERO ? [ZERO, ZERO] : [kidNodes[i]?.leftChild ?? ZERO, kidNodes[i]?.rightChild ?? ZERO])) as Address[];
  const g = useReadContracts({
    allowFailure: false,
    query: { enabled: grand.some((k) => k !== ZERO), refetchInterval: 15_000 },
    contracts: nodeReads(grand),
  });
  const grandNodes = (g.data ?? []) as unknown as NodeData[];
  const member = (address: Address, node?: NodeData): TreeMember => ({
    address,
    rank: node?.rank,
    leftUsd: node?.leftActiveUsd,
    rightUsd: node?.rightActiveUsd,
    ownUsd: node?.ownActiveUsd,
  });
  // heap order: the root, its two children, then the four below them; below an open slot there is nothing yet
  const slots: TreeSlot[] = [
    member(root, rn),
    ...kids.map((k, i) => (k === ZERO ? 'open' : member(k, kidNodes[i]))),
    ...grand.map((a, i) => (kids[i >> 1] === ZERO ? 'none' : a === ZERO ? 'open' : member(a, grandNodes[i]))),
  ] as TreeSlot[];
  while (slots.length < 7) slots.push(slots.length < 3 ? 'open' : 'none');
  return (
    <Card title={t('ref.tree')}>
      <p className="muted" style={{ fontSize: 12, marginTop: 0 }}>
        {t('ref.treeAbout')}
      </p>
      {root !== me ? (
        <Button size="sm" variant="ghost" onClick={() => setRoot(me)}>
          {t('ref.back')}
        </Button>
      ) : null}
      <div data-testid="tree">
        <Tree3D slots={slots} viewer={me} rankName={(n) => t(RANK_KEYS[n] ?? 'ref.member')} onOpen={setRoot} />
      </div>
    </Card>
  );
}

